import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CATEGORIES, PAYMENT_METHODS, formatBRL } from "@/lib/constants";
import { toast } from "sonner";
import { Sparkles, ArrowRight, ArrowLeft, Check } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import useGeoLocation from "@/lib/useGeoLocation";
import { MONETIZATION_ENABLED } from "@/lib/features";

const STEPS = ["Tipo & Produto", "Preço e pagamento", "Local e prazo"];

const ORDER_TYPES = [
  { v: "compra", label: "Compra", icon: "💰", desc: "Pago pelo item" },
  { v: "servico", label: "Serviço", icon: "🛠", desc: "Preciso de profissional" },
  { v: "permuta", label: "Permuta", icon: "🔄", desc: "Troco por outro item" },
  { v: "doacao", label: "Doação", icon: "🎁", desc: "Preciso/quero doar" },
];

export default function CreateOrder() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const geo = useGeoLocation();
  const [sp] = useSearchParams();
  const spType = sp.get("type");
  const spDirection = sp.get("direction");
  const initialType = ["compra", "permuta", "doacao", "servico"].includes(spType) ? spType : "compra";
  const initialDirection = spDirection === "dar" ? "dar" : "receber";
  const [step, setStep] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    order_type: initialType,
    offered_item: "",
    donation_direction: initialDirection,
    service_date: "",
    urgency_level: "media",
    service_location_type: "presencial",
    title: "",
    description: "",
    category: "",
    brand: "",
    quantity: 1,
    condition: "ambos",
    max_price: "",
    payment_method: "pix",
    city: user?.city || "",
    max_distance_km: 20,
    deadline_hours: 24,
    photos: [],
  });
  const [uploads, setUploads] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const askAi = async () => {
    if (!form.title || form.title.length < 3) {
      toast.error("Escreva o título do produto primeiro");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/suggest", {
        title: form.title,
        description: form.description,
      });
      setAiSuggestion(data);
      if (data.category && !form.category) set("category", data.category);
      toast.success("Sugestão da IA carregada!");
    } catch {
      toast.error("Falha ao consultar IA");
    }
    setAiLoading(false);
  };

  const submit = async () => {
    if (!form.title || !form.city) {
      toast.error("Preencha título e cidade");
      return;
    }
    if ((form.order_type === "compra" || form.order_type === "servico") && !form.max_price) {
      toast.error("Informe o valor");
      return;
    }
    if (form.order_type === "permuta" && !form.offered_item) {
      toast.error("Informe o que você oferece na troca");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        photos: uploads.map((u) => u.url),
        max_price: (form.order_type === "compra" || form.order_type === "servico") ? parseFloat(form.max_price) : 0,
        quantity: parseInt(form.quantity, 10) || 1,
        max_distance_km: parseInt(form.max_distance_km, 10) || 20,
        deadline_hours: parseInt(form.deadline_hours, 10) || 24,
        lat: geo.coords?.lat ?? null,
        lng: geo.coords?.lng ?? null,
      };
      const { data } = await api.post("/orders", payload);
      if (MONETIZATION_ENABLED && user?.has_free_boost) {
        toast.success("Pedido publicado em destaque! 🎁 Vendedores serão notificados.");
      } else {
        toast.success("Pedido publicado! Vendedores serão notificados.");
      }
      await refresh();
      nav(`/orders/${data.order_id}`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao publicar");
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-primary font-bold">Novo pedido</p>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight mt-1">
          {form.order_type === "servico" && "Contratar serviço"}
          {form.order_type === "permuta" && "Publicar troca"}
          {form.order_type === "doacao" && (form.donation_direction === "dar" ? "Fazer doação" : "Solicitar doação")}
          {form.order_type === "compra" && "Quero Comprar"}
        </h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div
              className={`flex items-center gap-2 ${i <= step ? "text-foreground" : "text-muted-foreground"}`}
            >
              <span
                className={`w-7 h-7 rounded-full flex items-center justify-center font-mono font-bold text-xs ${
                  i < step
                    ? "bg-primary text-primary-foreground"
                    : i === step
                    ? "bg-primary/20 text-primary border border-primary"
                    : "bg-secondary text-muted-foreground"
                }`}
              >
                {i < step ? <Check size={14} /> : i + 1}
              </span>
              <span className="text-xs font-medium hidden sm:block">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 md:p-6 space-y-4">
        {step === 0 && (
          <>
            <div>
              <Label>Tipo de pedido</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {ORDER_TYPES.map((t) => (
                  <button
                    key={t.v}
                    type="button"
                    data-testid={`order-type-${t.v}`}
                    onClick={() => set("order_type", t.v)}
                    className={`p-3 rounded-xl border text-left transition-colors ${
                      form.order_type === t.v
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="text-2xl">{t.icon}</div>
                    <div className={`font-heading font-bold text-sm mt-1 ${form.order_type === t.v ? "text-primary" : ""}`}>{t.label}</div>
                    <div className="text-[10px] text-muted-foreground">{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {form.order_type === "doacao" && (
              <div>
                <Label>Direção da doação</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {[
                    { v: "receber", l: "Preciso receber", i: "🙏" },
                    { v: "dar", l: "Quero doar", i: "❤️" },
                  ].map((d) => (
                    <button
                      key={d.v}
                      type="button"
                      data-testid={`donation-${d.v}`}
                      onClick={() => set("donation_direction", d.v)}
                      className={`h-12 rounded-xl border text-sm font-medium transition-colors ${
                        form.donation_direction === d.v
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      {d.i} {d.l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="title">
                {form.order_type === "permuta" && "O que você quer receber na troca?"}
                {form.order_type === "doacao" && form.donation_direction === "dar" && "O que você quer doar?"}
                {form.order_type === "doacao" && form.donation_direction === "receber" && "Do que você precisa?"}
                {form.order_type === "compra" && "O que você quer comprar?"}
                {form.order_type === "servico" && "Qual serviço você precisa?"}
              </Label>
              <Input
                id="title"
                data-testid="order-title"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder={
                  form.order_type === "servico"
                    ? "Ex: Eletricista para instalar chuveiro"
                    : "Ex: iPhone 13 Pro 128GB"
                }
                className="h-12"
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Descrição (opcional)</Label>
              <Textarea
                id="description"
                data-testid="order-description"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Detalhes, preferências, cor, tamanho..."
                rows={3}
              />
            </div>

            {(form.order_type === "compra" || form.order_type === "servico") && (
              <div>
                <Label htmlFor="brand">Marca (opcional)</Label>
                <Input
                  id="brand"
                  data-testid="order-brand"
                  value={form.brand}
                  onChange={(e) => set("brand", e.target.value)}
                  placeholder="Ex: Apple, Samsung, Bosch"
                  className="h-12"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Preenchendo, sua oportunidade chega direto aos fornecedores dessa marca.
                </p>
              </div>
            )}

            <div>
              <Label>Fotos, prints ou PDF (opcional)</Label>
              <FileUploader value={uploads} onChange={setUploads} max={6} />
            </div>

            {form.order_type === "permuta" && (
              <div>
                <Label htmlFor="offered">O que você oferece em troca?</Label>
                <Textarea
                  id="offered"
                  data-testid="order-offered-item"
                  value={form.offered_item}
                  onChange={(e) => set("offered_item", e.target.value)}
                  placeholder="Ex: bicicleta aro 29 usada em bom estado"
                  rows={2}
                />
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={askAi}
              disabled={aiLoading}
              data-testid="ai-suggest-btn"
              className="w-full gap-2 border-blue-500/30 text-blue-500 hover:bg-blue-500/10"
            >
              <Sparkles size={16} />
              {aiLoading ? "Analisando..." : "Sugestão da IA (categoria + preço médio)"}
            </Button>

            {aiSuggestion && (
              <div
                data-testid="ai-suggestion-box"
                className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-sm"
              >
                <p className="font-medium text-blue-500 mb-1">✨ Sugestão da IA</p>
                {aiSuggestion.category && (
                  <p><span className="text-muted-foreground">Categoria:</span> <strong>{aiSuggestion.category}</strong></p>
                )}
                {aiSuggestion.price != null && (
                  <p><span className="text-muted-foreground">Preço médio:</span> <strong>{formatBRL(aiSuggestion.price)}</strong></p>
                )}
                {aiSuggestion.product_name && (
                  <p><span className="text-muted-foreground">Produto:</span> {aiSuggestion.product_name}</p>
                )}
              </div>
            )}

            <div>
              <Label>Categoria</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`cat-${c}`}
                    onClick={() => set("category", c)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      form.category === c
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="quantity">Quantidade</Label>
                <Input
                  id="quantity"
                  data-testid="order-quantity"
                  type="number"
                  min={1}
                  value={form.quantity}
                  onChange={(e) => set("quantity", e.target.value)}
                  className="h-12"
                />
              </div>
              <div>
                <Label>Condição</Label>
                <div className="grid grid-cols-3 gap-1 h-12 bg-secondary p-1 rounded-lg">
                  {[
                    { v: "novo", l: "Novo" },
                    { v: "usado", l: "Usado" },
                    { v: "ambos", l: "Ambos" },
                  ].map((c) => (
                    <button
                      key={c.v}
                      type="button"
                      data-testid={`cond-${c.v}`}
                      onClick={() => set("condition", c.v)}
                      className={`rounded-md text-sm font-medium ${
                        form.condition === c.v
                          ? "bg-background shadow-sm"
                          : "text-muted-foreground"
                      }`}
                    >
                      {c.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            {(form.order_type === "compra" || form.order_type === "servico") ? (
              <>
                <div>
                  <Label htmlFor="max_price">
                    {form.order_type === "servico" ? "Quanto quer pagar pelo serviço (R$)" : "Valor máximo que pode pagar (R$)"}
                  </Label>
                  <Input
                    id="max_price"
                    data-testid="order-price"
                    type="number"
                    min={1}
                    step="0.01"
                    value={form.max_price}
                    onChange={(e) => set("max_price", e.target.value)}
                    placeholder="1500.00"
                    className="h-14 text-2xl font-heading font-black"
                    required
                  />
                  {form.max_price && (
                    <p className="text-sm text-muted-foreground mt-2 font-mono">
                      {formatBRL(parseFloat(form.max_price))}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Forma de pagamento</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {PAYMENT_METHODS.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        data-testid={`pay-${p.value}`}
                        onClick={() => set("payment_method", p.value)}
                        className={`h-12 rounded-xl border text-sm font-medium transition-colors ${
                          form.payment_method === p.value
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                {form.order_type === "servico" && (
                  <>
                    <div>
                      <Label htmlFor="service_date">Data desejada</Label>
                      <Input
                        id="service_date"
                        data-testid="service-date"
                        type="date"
                        value={form.service_date}
                        onChange={(e) => set("service_date", e.target.value)}
                        className="h-12"
                      />
                    </div>
                    <div>
                      <Label>Urgência</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {[
                          { v: "baixa", l: "Baixa", i: "🟢" },
                          { v: "media", l: "Média", i: "🟡" },
                          { v: "alta", l: "Alta", i: "🔴" },
                        ].map((u) => (
                          <button
                            key={u.v}
                            type="button"
                            data-testid={`urgency-${u.v}`}
                            onClick={() => set("urgency_level", u.v)}
                            className={`h-11 rounded-xl border text-sm font-medium transition-colors ${
                              form.urgency_level === u.v
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border hover:border-primary/50"
                            }`}
                          >
                            {u.i} {u.l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Atendimento</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {[
                          { v: "presencial", l: "Presencial" },
                          { v: "online", l: "Online" },
                          { v: "ambos", l: "Ambos" },
                        ].map((u) => (
                          <button
                            key={u.v}
                            type="button"
                            data-testid={`svcloc-${u.v}`}
                            onClick={() => set("service_location_type", u.v)}
                            className={`h-11 rounded-xl border text-sm font-medium transition-colors ${
                              form.service_location_type === u.v
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border hover:border-primary/50"
                            }`}
                          >
                            {u.l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="bg-secondary rounded-xl p-6 text-center" data-testid="no-payment-info">
                <p className="text-3xl mb-2">{form.order_type === "permuta" ? "🔄" : "🎁"}</p>
                <p className="font-heading font-bold text-lg">
                  {form.order_type === "permuta" ? "Troca sem dinheiro" : "Doação — sem cobrança"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {form.order_type === "permuta"
                    ? "Vendedores compatíveis vão negociar direto com você."
                    : "Este pedido não envolve pagamento em dinheiro."}
                </p>
              </div>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <div>
              <Label htmlFor="city">Cidade</Label>
              <Input
                id="city"
                data-testid="order-city"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="Ex: São Paulo"
                className="h-12"
                required
              />
            </div>
            <div>
              <Label htmlFor="dist">Raio de busca (km)</Label>
              <Input
                id="dist"
                data-testid="order-distance"
                type="number"
                min={1}
                value={form.max_distance_km}
                onChange={(e) => set("max_distance_km", e.target.value)}
                className="h-12"
              />
            </div>
            <div>
              <Label>Prazo do pedido</Label>
              <div className="grid grid-cols-4 gap-1 h-12 bg-secondary p-1 rounded-lg">
                {[6, 12, 24, 48].map((h) => (
                  <button
                    key={h}
                    type="button"
                    data-testid={`deadline-${h}`}
                    onClick={() => set("deadline_hours", h)}
                    className={`rounded-md text-sm font-medium ${
                      form.deadline_hours === h
                        ? "bg-background shadow-sm"
                        : "text-muted-foreground"
                    }`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-secondary rounded-xl p-4 text-sm">
              <p className="font-medium mb-2">Resumo</p>
              <p className="text-muted-foreground"><strong className="text-foreground">{form.title}</strong> — {form.category || "sem categoria"}</p>
              <p className="text-muted-foreground">Até <strong className="text-primary font-mono">{formatBRL(parseFloat(form.max_price || 0))}</strong> via <strong className="text-foreground">{form.payment_method.toUpperCase()}</strong></p>
              <p className="text-muted-foreground">{form.city} · {form.max_distance_km}km · {form.deadline_hours}h</p>
            </div>
            {MONETIZATION_ENABLED && user?.has_free_boost && (
              <div
                data-testid="boost-will-apply"
                className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/40 rounded-xl p-4 flex items-center gap-3"
              >
                <span className="text-2xl">🎁</span>
                <div className="text-sm">
                  <p className="font-heading font-bold">Este pedido vai em destaque</p>
                  <p className="text-xs text-muted-foreground">
                    Seu bônus de convite será aplicado automaticamente ao publicar.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex gap-2 pt-2">
          {step > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep((s) => s - 1)}
              data-testid="step-back"
              className="flex-1 h-12"
            >
              <ArrowLeft size={16} /> Voltar
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              data-testid="step-next"
              className="flex-1 h-12 font-bold"
            >
              Continuar <ArrowRight size={16} />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="order-submit"
              className="flex-1 h-12 font-bold"
            >
              {submitting ? "Publicando..." : "Publicar pedido"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
